import React, { useEffect, useState } from 'react'
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, IconButton, CircularProgress, Collapse, Tooltip
} from '@material-ui/core'
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import ExpandLessIcon from '@material-ui/icons/ExpandLess'
import { Alert } from '@material-ui/lab'
import { getStateMedicaidSavedRecords, deleteStateMedicaidSavedRecord } from 'actions/applicants'
import { Toast } from 'components/shared/Toaster'

const formatDate = (iso) => {
    if (!iso) return '—'
    try { return new Date(iso).toLocaleString() } catch { return iso }
}

// DOB persists as YYYYMMDD digit strings.
const formatCompactDate = (raw) => {
    if (!raw) return null
    const s = String(raw).trim()
    if (s.length !== 8 || !/^\d{8}$/.test(s)) return s
    return `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`
}

const reinstatedBadge = (record) => {
    if (record.isActive !== false) return null
    const formatted = record.reinstatementDate || null
    const style = {
        marginLeft: 6, padding: '1px 6px', fontSize: 10,
        borderRadius: 3, fontWeight: 600, verticalAlign: 'middle',
        background: 'var(--badge-approved-bg)', color: 'var(--status-approved)'
    }
    return (
        <span style={style} title='Exclusion was later lifted by the state Medicaid agency'>
            REINSTATED{formatted ? ` ${formatted}` : ''}
        </span>
    )
}

const ExpandableSavedRow = ({ record, onDelete }) => {
    const [open, setOpen] = useState(false)
    return (
        <>
            <TableRow hover style={{ cursor: 'pointer' }} onClick={() => setOpen(!open)}>
                <TableCell>
                    {record.matchedName || '—'}
                    {reinstatedBadge(record)}
                </TableCell>
                <TableCell>{record.entityType || '—'}</TableCell>
                <TableCell>{record.stateCode || '—'}</TableCell>
                <TableCell>{record.sourceName || '—'}</TableCell>
                <TableCell>{record.npi || '—'}</TableCell>
                <TableCell>{record.providerType || '—'}</TableCell>
                <TableCell>{record.suspensionDate || '—'}</TableCell>
                <TableCell>{record.activePeriodRaw || '—'}</TableCell>
                <TableCell align='right' padding='none' style={{ paddingRight: 8, whiteSpace: 'nowrap' }}>
                    <Tooltip title='Remove saved match' arrow>
                        <IconButton
                            size='small'
                            onClick={(e) => { e.stopPropagation(); onDelete(record.id) }}
                            aria-label='remove'
                        >
                            <DeleteOutlineIcon fontSize='small' />
                        </IconButton>
                    </Tooltip>
                    <IconButton
                        size='small'
                        aria-label={open ? 'collapse' : 'expand'}
                        onClick={(e) => { e.stopPropagation(); setOpen(!open) }}
                    >
                        {open ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={9}>
                    <Collapse in={open} timeout='auto' unmountOnExit>
                        <div style={{ padding: '12px 16px', fontSize: 13, lineHeight: 1.6 }}>
                            <div>
                                <strong>Status:</strong> {record.isActive === false ? `Reinstated${record.reinstatementDate ? ` on ${record.reinstatementDate}` : ''}` : 'Suspended'}
                                &nbsp;&nbsp; <strong>Type:</strong> {record.entityType || '—'}
                            </div>
                            <div>
                                <strong>Source:</strong> {record.sourceName || '—'}
                                &nbsp;&nbsp; <strong>State:</strong> {record.stateCode || '—'}
                            </div>
                            <div>
                                <strong>Suspension Date:</strong> {record.suspensionDate || '—'}
                                &nbsp;&nbsp; <strong>Active period:</strong> {record.activePeriodRaw || '—'}
                            </div>
                            {record.providerType && (
                                <div><strong>Provider Type:</strong> {record.providerType}</div>
                            )}
                            {record.npi && (
                                <div><strong>NPI:</strong> {record.npi}</div>
                            )}
                            {record.licenseNumber && (
                                <div><strong>License #:</strong> {record.licenseNumber}</div>
                            )}
                            {record.providerNumbers && (
                                <div><strong>Provider #s:</strong> {record.providerNumbers}</div>
                            )}
                            {formatCompactDate(record.dob) && (
                                <div><strong>DOB:</strong> {formatCompactDate(record.dob)}</div>
                            )}
                            <div>
                                <strong>Address:</strong>{' '}
                                {[record.address, record.city, record.state, record.zip].filter(Boolean).join(', ') || '—'}
                            </div>
                            <div><strong>Saved:</strong> {formatDate(record.createdAt)}</div>
                            {record.notes && (
                                <div style={{ marginTop: 6 }}><strong>Notes:</strong> <span style={{ wordBreak: 'break-word' }}>{record.notes}</span></div>
                            )}
                        </div>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    )
}

const StateMedicaidSavedRecords = ({ applicantId, refreshKey, onRecordDeleted }) => {
    const [records, setRecords] = useState([])
    const [loading, setLoading] = useState(false)

    const fetch = () => {
        setLoading(true)
        getStateMedicaidSavedRecords({ applicationId: applicantId })()
            .then(res => setRecords(Array.isArray(res) ? res : []))
            .catch(() => Toast({ type: 'error', message: 'Failed to load saved records' }))
            .finally(() => setLoading(false))
    }

    useEffect(() => {
        if (applicantId) fetch()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicantId, refreshKey])

    const handleDelete = (recordId) => {
        deleteStateMedicaidSavedRecord({ applicationId: applicantId, recordId })()
            .then(() => {
                Toast({ type: 'success', message: 'Record removed' })
                fetch()
                if (typeof onRecordDeleted === 'function') onRecordDeleted()
            })
            .catch(() => Toast({ type: 'error', message: 'Failed to remove record' }))
    }

    if (!applicantId) return null

    return (
        <div className='cardStyle mt-3'>
            <div className='uccTitleContainer'><h5>Saved State Medicaid Matches</h5></div>
            {loading ? (
                <div style={{ padding: 24, textAlign: 'center' }}><CircularProgress size={20} /></div>
            ) : records.length === 0 ? (
                <Alert severity='info'>No State Medicaid exclusions saved to this application yet.</Alert>
            ) : (
                <TableContainer component={Paper}>
                    <Table size='small'>
                        <TableHead>
                            <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>State</TableCell>
                                <TableCell>Source</TableCell>
                                <TableCell>NPI</TableCell>
                                <TableCell>Provider Type</TableCell>
                                <TableCell>Suspension Date</TableCell>
                                <TableCell>Active Period</TableCell>
                                <TableCell />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {records.map(r => (
                                <ExpandableSavedRow key={r.id} record={r} onDelete={handleDelete} />
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </div>
    )
}

export default StateMedicaidSavedRecords
